/**
 * Image Optimization Service for KUINI Website
 * Handles lazy loading, progressive loading, and WebP detection
 * Improves Core Web Vitals scores by optimizing image delivery
 */

/**
 * Initialize intersection observer for lazy loading images
 */
export function initializeLazyLoading() {
    // Check if IntersectionObserver is supported
    if (!('IntersectionObserver' in window)) {
        console.log('📷 IntersectionObserver not supported, loading all images immediately');
        loadAllImages();
        return;
    }

    const imageObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                const img = entry.target;
                loadImage(img);
                observer.unobserve(img);
            }
        });
    }, {
        rootMargin: '50px 0px',
        threshold: 0.01
    });

    // Observe all images with data-src attribute
    const lazyImages = document.querySelectorAll('img[data-src]');
    lazyImages.forEach(img => imageObserver.observe(img));

    console.log(`📷 Lazy loading initialized for ${lazyImages.length} images`);
}

/**
 * Load a single image with error handling and progressive enhancement
 * @param {HTMLImageElement} img - The image element to load
 */
function loadImage(img) {
    const src = img.dataset.src;
    const srcset = img.dataset.srcset;

    if (!src) return;

    // Create a new image to preload
    const imageLoader = new Image();

    imageLoader.onload = () => {
        // Add fade-in effect
        img.style.opacity = '0';
        img.style.transition = 'opacity 0.3s ease-in-out';

        img.src = src;
        if (srcset) img.srcset = srcset;

        // Trigger fade-in
        requestAnimationFrame(() => {
            img.style.opacity = '1';
        });

        // Remove data attributes to prevent reprocessing
        delete img.dataset.src;
        delete img.dataset.srcset;

        img.classList.add('loaded');
    };

    imageLoader.onerror = () => {
        console.warn('📷 Failed to load image:', src);
        img.classList.add('error');
    };

    // Start loading
    imageLoader.src = src;
}

/**
 * Fallback for browsers without IntersectionObserver
 */
function loadAllImages() {
    const lazyImages = document.querySelectorAll('img[data-src]');
    lazyImages.forEach(loadImage);
}

/**
 * Check if browser supports WebP format
 * @returns {Promise<boolean>} True if WebP is supported
 */
export function supportsWebP() {
    return new Promise((resolve) => {
        const webP = new Image();
        webP.onload = webP.onerror = () => {
            resolve(webP.height === 2);
        };
        webP.src = 'data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA';
    });
}

/**
 * Create optimized image source URLs based on device capabilities
 * @param {string} baseSrc - Base image source
 * @param {boolean} useWebP - Whether to use WebP format
 * @returns {Object} Object with src and srcset
 */
export function createOptimizedImageSources(baseSrc, useWebP = false) {
    const extension = useWebP ? '.webp' : '.png';
    const baseName = baseSrc.replace(/\.[^/.]+$/, '');

    return {
        src: `${baseName}${extension}`,
        srcset: `
            ${baseName}-small${extension} 320w,
            ${baseName}-medium${extension} 640w,
            ${baseName}-large${extension} 1280w,
            ${baseName}${extension} 1920w
        `.replace(/\s+/g, ' ').trim()
    };
}

/**
 * Initialize progressive image loading with WebP detection
 */
export async function initializeProgressiveImageLoading() {
    console.log('📷 Initializing progressive image loading...');

    try {
        // Check WebP support
        const webPSupported = await supportsWebP();
        console.log(`📷 WebP support: ${webPSupported ? 'Yes' : 'No'}`);

        // Initialize lazy loading
        initializeLazyLoading();

        // Add loading states and error handling
        addImageLoadingStates();

        console.log('✅ Progressive image loading initialized successfully');
    } catch (error) {
        console.error('❌ Failed to initialize progressive image loading:', error);
        // Fallback to standard loading
        loadAllImages();
    }
}

/**
 * Add loading states and error handling to images
 */
function addImageLoadingStates() {
    const images = document.querySelectorAll('img');

    images.forEach(img => {
        // Add loading placeholder if not already loaded
        if (!img.complete || img.naturalHeight === 0) {
            img.style.backgroundColor = '#f3f4f6';
            img.style.backgroundImage = 'linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%)';
            img.style.backgroundSize = '200% 100%';
            img.style.animation = 'shimmer 1.5s infinite linear';
        }

        // Add load event listener
        img.addEventListener('load', () => {
            img.style.backgroundColor = '';
            img.style.backgroundImage = '';
            img.style.animation = '';
        });

        // Add error event listener
        img.addEventListener('error', () => {
            img.style.backgroundColor = '#fee2e2';
            img.style.backgroundImage = '';
            img.style.animation = '';
            console.warn('📷 Image failed to load:', img.src);
        });
    });
}

/**
 * Preload critical images for better LCP
 * @param {string[]} imageUrls - Array of critical image URLs
 */
export function preloadCriticalImages(imageUrls) {
    imageUrls.forEach(url => {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = url;
        document.head.appendChild(link);
    });

    console.log(`🚀 Preloaded ${imageUrls.length} critical images`);
}